import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Strike from "@tiptap/extension-strike";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Subscript from "@tiptap/extension-subscript";

import Superscript from "@tiptap/extension-superscript";
import CustomDropdown from "./CustomDropdown";
import FontSize from "./FontSize"; 
import "./TextEditor.css";
import { HexColorPicker } from "react-colorful";
import {
  Italic,
  BoldIcon,
  List,
  Palette,
  Heading as HeadingIcon,
  TextIcon,
  Strikethrough,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Superscript as SuperscriptIcon,
  Subscript as SubscriptIcon,
  Link2,
  ExternalLink,
  Trash,
  Heading1,
  Type
} from "lucide-react";
import Underline from "@tiptap/extension-underline";
// import Superscript from "@tiptap/extension-superscript";
// import Subscript from "@tiptap/extension-subscript";
import Code from "@tiptap/extension-code";
import CodeBlock from "@tiptap/extension-code-block";
import { createPopper } from "@popperjs/core";
import { createPortal } from "react-dom";

const getStyledHtmlFromEditor = (editor) => {
  let html = editor.getHTML();
  html = html
    .replace(
      /<h1>/g,
      '<h1 style="font-size: 2em; margin: 0.5em 0; font-weight: bold;">'
    )
    .replace(
      /<h2>/g,
      '<h2 style="font-size: 1.75em; margin: 0.5em 0; font-weight: bold;">'
    )
    .replace(
      /<h3>/g,
      '<h3 style="font-size: 1.5em; margin: 0.5em 0; font-weight: bold;">'
    )
    .replace(
      /<h4>/g,
      '<h4 style="font-size: 1.25em; margin: 0.5em 0; font-weight: bold;">'
    )
    .replace(
      /<h5>/g,
      '<h5 style="font-size: 1.1em; margin: 0.5em 0; font-weight: bold;">'
    )
    .replace(
      /<h6>/g,
      '<h6 style="font-size: 1em; margin: 0.5em 0; font-weight: bold;">'
    )
    .replace(
      /<p>/g,
      '<p style="margin: 0 0 1em 0; font-size: 14px; line-height: 1.6;">'
    )
    .replace(/<ul>/g, '<ul style="margin: 0 0 1em 1.25em; list-style: disc;">')
    .replace(
      /<ol>/g,
      '<ol style="margin: 0 0 1em 1.25em; list-style: decimal;">'
    )
    .replace(/<li>/g, '<li style="margin-bottom: 0.25em;">')
    .replace(/<strong>/g, '<strong style="font-weight: bold;">')
    .replace(/<em>/g, '<em style="font-style: italic;">')
    .replace(
      /<blockquote>/g,
      '<blockquote style="border-left: 3px solid #ccc; padding-left: 1em; margin: 1em 0; color: #555;">'
    );

  return `<div style="font-family: sans-serif;">${html}</div>`;
};

const alignOptions = [
  { Field: "left", label: "Left", icon: <AlignLeft size={14} /> },
  { Field: "center", label: "Center", icon: <AlignCenter size={14} /> },
  { Field: "right", label: "Right", icon: <AlignRight size={14} /> },
  { Field: "justify", label: "Justify", icon: <AlignJustify size={14} /> },
];

const TextEditor = ({ value, onChange,fullHeight=false, showEditorInitially = false }) => {
  const editorRef = useRef(null);
  const colorPickerRef = useRef(null);
  const [showEditor, setShowEditor] = useState(showEditorInitially);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [textColor, setTextColor] = useState("#000000");
  const [textOpacity, setTextOpacity] = useState(1);
  const [previewHtml, setPreviewHtml] = useState(value || "");
  const [selected, setSelected] = useState("paragraph");
  const [fontSize, setFontSize] = useState("16px");
  const [isFontDropdownOpen, setFontDropdownOpen] = useState(false);
  const [isHeadingDropdownOpen, setHeadingDropdownOpen] = useState(false);
  const [alignment, setAlignment] = useState("left");
  const [isAlignDropdownOpen, setAlignDropdownOpen] = useState(false);
  const colorButtonRef = useRef(null);
  const colorDropdownRef = useRef(null);
  const [linkData, setLinkData] = useState({ href: "", pos: null });
  const [showLinkOptions, setShowLinkOptions] = useState(false);
  const [isLinkDropdownOpen, setIsLinkDropdownOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const linkButtonRef = useRef(null);
  const linkDropdownRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: {
          HTMLAttributes: {}, 
        },
      }),
      TextStyle,
      Color,
      TextAlign.configure({
        types: ["heading", "paragraph"],
        alignments: ["left", "center", "right", "justify"],
      }),
      Mention.configure({
        HTMLAttributes: { class: "mention" },
        suggestion,
        renderText: ({ node }) => `@${node.attrs.label}`,
        renderHTML: ({ node }) => [
          "span",
          {
            class: "mention",
            "data-mention-id": node.attrs.id,
            contenteditable: "false",
          },
          `@${node.attrs.label}`,
        ],
      }),
      Link.configure({
        openOnClick: false, 
        autolink: false,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer",
          class: "text-blue-600 underline hover:text-blue-800 cursor-pointer",
        },
      }),
      Superscript,
      Subscript,
      Underline,
      FontSize,
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const styled = getStyledHtmlFromEditor(editor);
      //onChange(styled);
      onChange(editor.getHTML());
      setPreviewHtml(styled);
    },
  });
  useEffect(() => {
  if (editor && value !== editor.getHTML()) {
    editor.commands.setContent(value || '', false); // false disables auto-focus
     const styled = getStyledHtmlFromEditor(editor);
    setPreviewHtml(styled);
  }
}, [value, editor]);
  // Colorpalette-----
  useEffect(() => {
    if (colorPickerOpen && colorButtonRef.current && colorDropdownRef.current) {
      createPopper(colorButtonRef.current, colorDropdownRef.current, {
        placement: "bottom-start",
        modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
      });
    }
  }, [colorPickerOpen]);
  // Link dropdown positioning popper-----
  useEffect(() => {
    if (
      isLinkDropdownOpen &&
      linkButtonRef.current &&
      linkDropdownRef.current
    ) {
      createPopper(linkButtonRef.current, linkDropdownRef.current, {
        placement: "bottom-start",
        modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
      });
    }
  }, [isLinkDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        linkDropdownRef.current &&
        !linkDropdownRef.current.contains(e.target) &&
        !linkButtonRef.current.contains(e.target)
      ) {
        setIsLinkDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (!editor || !editor.view) return;

      const pos = editor.view.posAtCoords({
        left: e.clientX,
        top: e.clientY,
      });

      if (!pos) return;

      const $pos = editor.view.state.doc.resolve(pos.pos);
      const marks = $pos.marks();

      const linkMark = marks.find((mark) => mark.type.name === "link");

      if (linkMark) {
        e.preventDefault();

        const clickedPos = $pos.pos;

        if (isLinkDropdownOpen && linkData?.pos === clickedPos) {
          // Already open for this link, so toggle it closed
          setIsLinkDropdownOpen(false);
          setLinkData(null);
        } else {
          // Open or update for a new link
          setLinkUrl(linkMark.attrs.href);
          setLinkData({ href: linkMark.attrs.href, pos: clickedPos });
          setIsLinkDropdownOpen(true);
        }
      } else {
        // Clicked outside a link
        setIsLinkDropdownOpen(false);
        setLinkData(null);
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [editor, isLinkDropdownOpen, linkData]);

  // Link options-----
  useEffect(() => {
    const attrs = editor?.getAttributes("link");
    if (attrs?.href) setLinkUrl(attrs.href);
  }, [showLinkOptions]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Hide editor if clicked outside editor
      if (editorRef.current && !editorRef.current.contains(e.target)) {
        if (!showEditorInitially) setShowEditor(false);
      }

      // Close color picker if click is outside both button and dropdown
      if (
        colorButtonRef.current &&
        !colorButtonRef.current.contains(e.target) &&
        colorDropdownRef.current &&
        !colorDropdownRef.current.contains(e.target)
      ) {
        setColorPickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showEditorInitially]);

  if (!editor)
    return <div className="text-sm text-gray-400">Loading editor...</div>;

  const fontSizes = ["12px", "14px", "16px", "18px", "24px"];
  const applyFontSize = (value) => {
    setFontSize(value);
    editor.chain().focus().setFontSize({ fontSize: value }).run();
  };

  const headingOptions = [
    ...[1, 2, 3, 4, 5, 6].map((n) => ({ Field: `heading-${n}` })),
  ];

  const handleSelect = (val) => {
    if (!val || typeof val !== "string") return;
    if (val === selected || val === "paragraph") {
      setSelected("paragraph");
      editor.chain().focus().setParagraph().run();
    } else {
      const level = parseInt(val.replace("heading-", ""));
      setSelected(val);
      editor.chain().focus().toggleHeading({ level }).run();
    }
  };

  // Textalign----
  const applyAlignment = (align) => {
    editor.chain().focus().setTextAlign(align).run();
  };

  // For heading----
  function getHeadingClass(level) {
    switch (level) {
      case 1:
        return "text-2xl";
      case 2:
        return "text-xl";
      case 3:
        return "text-lg";
      case 4:
        return "text-base";
      case 5:
        return "text-sm";
      case 6:
        return "text-xs";
      default:
        return "text-sm";
    }
  }

  return (
    <div className="relative">
      <div ref={editorRef} 
             className={`${fullHeight ? "" : "ws-edior border rounded"} px-2 py-1`}>
        {showEditor ? (
          <>
            <div className="sticky top-0 z-40 bg-white border-b pb-2 mb-2 overflow-x-auto scrollbar-hide">
              <div className="flex w-max gap-3 px-2 text-sm">
                {" "}
                {/* Bold */}
                <button
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  title="Ctrl+B"
                  className={`h-6 w-6 min-w-6 min-h-6 p-1 rounded flex items-center justify-center ${
                    editor.isActive("bold")
                      ? "bg-blue-100 text-blue-600 font-semibold"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <BoldIcon size={14} />
                </button>
                {/* Italic */}
                <button
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  title="Ctrl+I"
                  className={`h-6 w-6 min-w-6 min-h-6 p-1 rounded flex items-center justify-center ${
                    editor.isActive("italic")
                      ? "bg-blue-100 text-blue-600 font-semibold"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <Italic size={14} />
                </button>
                {/* Underline */}
                <button
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  title="Underline"
                  className={`h-6 w-6 min-w-6 min-h-6 p-1 rounded flex items-center justify-center ${
                    editor.isActive("underline")
                      ? "bg-blue-100 text-blue-600 font-semibold"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <u className="text-sm font-medium">U</u>
                </button>
                {/* Unordered List */}
                <button
                  onClick={() =>
                    editor.chain().focus().toggleBulletList().run()
                  }
                  className={`h-6 w-6 min-w-6 min-h-6 p-1 rounded flex items-center justify-center ${
                    editor.isActive("bulletList")
                      ? "bg-blue-100 text-blue-600 font-semibold"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <List className="stroke-1" size={20} title="Bullets" />
                </button>
                {/* Ordered List */}
                <button
                  onClick={() =>
                    editor.chain().focus().toggleOrderedList().run()
                  }
                  className={`h-6 w-6 min-w-6 min-h-6 p-1 rounded flex items-center justify-center ${
                    editor.isActive("orderedList")
                      ? "bg-blue-100 text-blue-600 font-semibold"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <ListOrdered
                    className="text-black stroke-1"
                    size={20}
                    title="Numbered List"
                  />
                </button>
                {/* Color Picker */}
                <div className="relative flex items-center">
                  <button
                    ref={colorButtonRef}
                    onClick={() => setColorPickerOpen(!colorPickerOpen)}
                    className={`h-6 w-6 min-w-6 min-h-6 p-1 rounded flex items-start justify-center ${
                      colorPickerOpen
                        ? "bg-blue-100 text-blue-600 font-semibold"
                        : "hover:bg-gray-100 text-gray-700"
                    }`}
                    title="Text Color"
                  >
                    <Palette className="stroke-1" size={18} />
                  </button>
                </div>
                {colorPickerOpen &&
                  createPortal(
                    <div
                      ref={colorDropdownRef}
                      className="z-[9999] bg-white border rounded shadow p-2"
                      style={{ position: "absolute" }}
                    >
                      <HexColorPicker
                        color={textColor}
                        onChange={(color) => {
                          setTextColor(color);
                          const rgba = hexToRgba(color, textOpacity); 
                          editor.chain().focus().setColor(rgba).run();
                        }}
                      />

                      <div className="mt-2 text-xs text-gray-600">Opacity</div>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={textOpacity}
                        onChange={(e) => {
                          setTextOpacity(e.target.value);
                          const rgba = hexToRgba(textColor, e.target.value);
                          editor.chain().focus().setColor(rgba).run();
                        }}
                        className="w-full"
                      />
                    </div>,
                    document.body
                  )}
                {/* Font Size Dropdown */}
                <CustomDropdown
                  filteredFields={fontSizes.map((size) => ({ Field: size }))}
                  addFilter={(fieldObj) => {
                    applyFontSize(fieldObj.Field);
                    setFontDropdownOpen(false); // close on select
                  }}
                  // getColumnLabel={(field) =>
                  //   field === "16px" ? "Font Size" : field
                  // }
                  noSearch
                  onOpen={() => {
                    setColorPickerOpen(false);
                    setFontDropdownOpen(true);
                  }}
                  onClose={() => setFontDropdownOpen(false)}
                  triggerIcon={
                    <div
                      className={`h-6 w-10 min-w-10 min-h-6 p-1 rounded flex items-center justify-center text-xs transition
        ${
          isFontDropdownOpen
            ? "bg-blue-100 text-blue-600 font-semibold"
            : "hover:bg-gray-100"
        }
       `}
                    >
                      {fontSize === "16px" ? (
                        <>
                          <Type size={17} />
                        </>
                      ) : (
                        <span>{fontSize}</span>
                      )}
                    </div>
                  }
                />
                {/* Heading Dropdown */}
                <CustomDropdown
                  filteredFields={[...headingOptions]}
                  addFilter={(fieldObj) => {
                    handleSelect(
                      typeof fieldObj === "string" ? fieldObj : fieldObj.Field
                    );
                    setHeadingDropdownOpen(false); // close dropdown
                  }}
                  getColumnLabel={(field) => {
                    if (field === "paragraph") {
                      return <span className="text-sm">Paragraph</span>;
                    }

                    const level = parseInt(field.replace("heading-", ""));
                    const tag = `h${level}`;

                    return React.createElement(
                      tag,
                      {
                        className: `m-0 font-medium ${getHeadingClass(level)}`,
                      },
                      `Heading ${level}`
                    );
                  }}
                  noSearch
                  onOpen={() => {
                    setColorPickerOpen(false);
                    setHeadingDropdownOpen(true);
                  }}
                  onClose={() => setHeadingDropdownOpen(false)}
                  triggerIcon={
                    <div
                      className={`h-6 w-8 min-w-8 min-h-6 p-1 rounded flex items-center justify-center text-xs font-medium transition ${
                        isHeadingDropdownOpen
                          ? "bg-blue-100 text-blue-600 font-semibold"
                          : "bg-white text-gray-700 hover:bg-gray-100"
                      }`}>
                      <Heading1 size={16} />
                    </div>
                  }
                />
                {/* {Strike} */}
                <button
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  title="Strike-through"
                  className={`h-6 w-6 min-w-6 min-h-6 p-1 rounded flex items-center justify-center ${
                    editor.isActive("strike")
                      ? "bg-blue-100 text-blue-600 font-semibold"
                      : "hover:bg-gray-100"
                  }`}
                >
                  <Strikethrough size={18} />
                </button>
                {/* ---Text Alignment---- */}
                <CustomDropdown
                  filteredFields={alignOptions.map(({ Field }) => ({
                    Field,
                  }))}
                  addFilter={(fieldObj) => {
                    const value = fieldObj.Field;
                    editor.chain().focus().setTextAlign(value).run();
                    setAlignment(value);
                    setAlignDropdownOpen(false);
                  }}
                  getColumnLabel={(field) => {
                    const found = alignOptions.find(
                      (opt) => opt.Field === field
                    );
                    return (
                      <div className="flex items-center gap-2">
                        {found?.icon}
                        {found?.label}
                      </div>
                    );
                  }}
                  noSearch
                  onOpen={() => {
                    setColorPickerOpen(false);
                    setAlignDropdownOpen(true);
                  }} 
                  onClose={() => setAlignDropdownOpen(false)}
                  triggerIcon={
                    <div
                      className={`h-6 w-6 min-w-6 min-h-6 p-1 rounded flex items-center justify-center gap-1 text-sm transition
        ${
          isAlignDropdownOpen
            ? "bg-blue-100 text-blue-600 font-semibold"
            : "bg-white text-gray-700 hover:bg-gray-100"
        }`}
                    >
                      {alignOptions.find((opt) => opt.Field === alignment)
                        ?.icon ?? <AlignLeft size={18} />}
                    </div>
                  }
                />
                {/* Link----- */}
                <div className="relative flex items-start">
                  <button
                    ref={linkButtonRef}
                    onClick={() => {
                      const currentHref =
                        editor.getAttributes("link")?.href || "";
                      setLinkUrl(currentHref);
                      setIsLinkDropdownOpen((prev) => !prev);
                    }}
                    title="Insert Link"
                    className={`h-6 w-6 min-w-6 min-h-6 p-1 rounded flex items-center justify-center ${
                      editor.isActive("link")
                        ? "bg-blue-100 text-blue-600 font-semibold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <Link2 size={20} />
                  </button>
                  {isLinkDropdownOpen &&
                    createPortal(
                      <div
                        ref={linkDropdownRef}
                        onClick={(e) => e.stopPropagation()}
                        className="z-[9999] bg-white border shadow p-3 rounded w-72"
                        style={{ position: "absolute" }}
                      >
                        {/* URL Input */}
                        <div className="mb-2">
                          <input
                            type="text"
                            placeholder="Enter URL"
                            value={linkUrl}
                            onChange={(e) => setLinkUrl(e.target.value)}
                            className="border px-3 py-2 rounded text-sm w-full outline-none focus:ring-2 focus:ring-blue-200"
                          />
                        </div>

                        {/* Actions: Apply | Open | Remove */}
                        <div className="flex justify-around mt-1 text-lg text-gray-600">
                          {/* Apply */}
                          <button
                            title="Apply Link"
                            onClick={() => {
                              if (linkUrl) {
                                editor
                                  .chain()
                                  .focus()
                                  .extendMarkRange("link")
                                  .setLink({ href: linkUrl })
                                  .run();
                              }
                              setIsLinkDropdownOpen(false);
                              setLinkUrl("");
                            }}
                            className="hover:text-blue-600 transition"
                          >
                            <Link2 size={16} />
                          </button>

                          {/* Open in New Tab */}
                          <button
                            title="Open in New Tab"
                            onClick={() => {
                              const attrs = editor.getAttributes("link");
                              if (attrs?.href)
                                window.open(attrs.href, "_blank");
                            }}
                            className="hover:text-green-600 transition"
                          >
                            <ExternalLink size={16} />
                          </button>

                          {/* Remove */}
                          <button
                            title="Remove Link"
                            onClick={() => {
                              editor.chain().focus().unsetLink().run();
                              setIsLinkDropdownOpen(false);
                              setLinkUrl("");
                            }}
                            className="hover:text-red-500 transition"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </div>,
                      document.body
                    )}
                </div>
                {/* Superscript Button */}
                <button
                  title="Superscript"
                  onClick={() =>
                    editor.chain().focus().unsetSubscript().toggleSuperscript().run()
                  }
                  className={`h-6 w-6 min-w-6 min-h-6 p-1 rounded flex items-center justify-center  ${
                    editor.isActive("superscript")
                      ? "bg-blue-100 text-blue-600 font-semibold"
                      : "hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  <SuperscriptIcon size={18} />
                </button>
                <button
                  title="Subscript"
                  onClick={() => editor.chain().focus().unsetSuperscript().toggleSubscript().run()}
                  className={`h-6 w-6 min-w-6 min-h-6 p-1 rounded flex items-center justify-center  ${
                    editor.isActive("subscript")
                      ? "bg-blue-100 text-blue-600 font-semibold"
                      : "hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  <SubscriptIcon size={18} />
                </button>
                {/* tools above this line-- */}
              </div>
            </div>

            {/* Editor Content */}
            <div 
             className={`${fullHeight ? "" : "max-h-[300px] overflow-y-auto"} px-2 py-1`}>
              <EditorContent editor={editor} className="tiptap" />
            </div>
          </>
        ) : (
          <div
            className="max-h-[300px] overflow-y-auto px-2 py-1 text-sm text-gray-700 cursor-pointer"
            onClick={() => setShowEditor(true)}
            dangerouslySetInnerHTML={{
              __html:
                previewHtml ||
                '<span class="italic text-gray-400">Click to add content...</span>',
            }}
          />
        )}
      </div>
    </div>
  );
};

function hexToRgba(hex, opacity) {
  const bigint = parseInt(hex.replace("#", ""), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

const dummyMentions = [
  { id: "1", label: "Alice" },
  { id: "2", label: "Bob" },
  { id: "3", label: "Charlie" },
  { id: "4", label: "David" },
  { id: "5", label: "Eve" },
  { id: "6", label: "Frank" },
  { id: "7", label: "Grace" },
  { id: "8", label: "Heidi" },
  { id: "9", label: "Ivan" },
  { id: "10", label: "Judy" },
];

const suggestion = {
  items: ({ query }) =>
    dummyMentions.filter((item) =>
      item.label.toLowerCase().includes(query.toLowerCase())
    ),
  render: () => {
    let selectedIndex = 0;
    let component;
    let popup;

    return {
      onStart: (props) => {
        component = document.createElement("div");
        component.classList.add("mention-popup");
        component.style.cssText = `
          position: absolute;
          z-index: 9999;
          background: white;
          border: 1px solid #ccc;
          border-radius: 4px;
          padding: 4px;
          font-size: 14px;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
        `;

        props.items.forEach((item) => {
          const el = document.createElement("div");
          el.className = "mention-item";
          el.textContent = item.label;
          el.onclick = () => props.command({ id: item.id, label: item.label });
          el.style.cssText = "padding: 4px 8px; cursor: pointer;";
          el.onmouseover = () => (el.style.backgroundColor = "#f0f0f0");
          el.onmouseout = () => (el.style.backgroundColor = "transparent");
          component.appendChild(el);
        });

        popup = document.body.appendChild(component);

        const onKeyDown = (e) => {
          if (!component || !props) return;
          if (e.key === "ArrowDown") {
            selectedIndex = (selectedIndex + 1) % props.items.length;
            selectItem(selectedIndex);
            e.preventDefault();
          } else if (e.key === "ArrowUp") {
            selectedIndex =
              (selectedIndex - 1 + props.items.length) % props.items.length;
            selectItem(selectedIndex);
            e.preventDefault();
          } else if (e.key === "Enter") {
            props.command(props.items[selectedIndex]);
            popup?.remove();
            document.removeEventListener("keydown", onKeyDown);
            e.preventDefault();
          }
        };

        const selectItem = (index) => {
          const items = component.querySelectorAll(".mention-item");
          items.forEach((el, i) => {
            el.style.backgroundColor = i === index ? "#e0e7ff" : "transparent";
          });
        };

        document.addEventListener("keydown", onKeyDown);
        popup = document.body.appendChild(component);
        const { left, top } = props.clientRect();
        popup.style.left = `${left}px`;
        popup.style.top = `${top + 25}px`;

        selectItem(selectedIndex);
      },
      onUpdate: (props) => {
        component.innerHTML = "";
        props.items.forEach((item) => {
          const el = document.createElement("div");
          el.className = "mention-item";
          el.textContent = item.label;
          el.onclick = () => props.command({ id: item.id, label: item.label });
          el.style.cssText = "padding: 4px 8px; cursor: pointer;";
          el.onmouseover = () => (el.style.backgroundColor = "#f0f0f0");
          el.onmouseout = () => (el.style.backgroundColor = "transparent");
          component.appendChild(el);
        });
      },
      onExit: () => {
        if (popup && popup.remove) popup.remove();
      },
    };
  },
};

export default TextEditor;
